import { useEffect, useRef, useState } from 'react';

const SCRAMBLE_CHARS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/=';
const STAGGER_MS = 14; // delay before each character starts settling
const CHAR_SCRAMBLE_MS = 220; // how long a character flickers before it settles
const CYCLE_MS = 3600; // how long each state (locked / unlocked) holds

function randomChar() {
  return SCRAMBLE_CHARS[Math.floor(Math.random() * SCRAMBLE_CHARS.length)];
}
function randomCipher(length) {
  let out = '';
  for (let i = 0; i < length; i++) out += randomChar();
  return out;
}
function prefersReducedMotion() {
  return typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
}

const LINES = [
  'Case notes from your review.',
  'Held by the council since 2011.',
  'Reviewed every six months.',
  'Signed off by your social worker.',
  'Filed under section 47.',
];

/**
 * One line that unscrambles into plain text (or scrambles back into
 * cipher), staying the same brand-green colour throughout — only the
 * characters change, never the styling, so it never reads as "redacted".
 * `unlocked` is shared across every line and the padlock so they all
 * settle together.
 */
const ScrambleLine = ({ text, unlocked }) => {
  const [display, setDisplay] = useState(() => randomCipher(text.length));
  const frameRef = useRef(null);
  const startRef = useRef(null);
  const lockedCipherRef = useRef(randomCipher(text.length));

  useEffect(() => {
    if (!unlocked) lockedCipherRef.current = randomCipher(text.length);
  }, [unlocked, text.length]);

  useEffect(() => {
    if (prefersReducedMotion()) {
      setDisplay(unlocked ? text : lockedCipherRef.current);
      return undefined;
    }
    startRef.current = null;
    const finalString = unlocked ? text : lockedCipherRef.current;
    const tick = (t) => {
      if (startRef.current == null) startRef.current = t;
      const elapsed = t - startRef.current;
      let out = '';
      let allSettled = true;
      for (let i = 0; i < text.length; i++) {
        if (text[i] === ' ') {
          out += ' ';
          continue;
        }
        const settleAt = i * STAGGER_MS + CHAR_SCRAMBLE_MS;
        if (elapsed < settleAt) {
          out += randomChar();
          allSettled = false;
        } else {
          out += finalString[i];
        }
      }
      setDisplay(out);
      if (!allSettled) frameRef.current = requestAnimationFrame(tick);
    };
    frameRef.current = requestAnimationFrame(tick);
    return () => frameRef.current && cancelAnimationFrame(frameRef.current);
  }, [unlocked, text]);

  return <span className="pd-decode-stable pd-wrap-anywhere text-brand-ink">{display}</span>;
};

const BackSheet = ({ rotate }) => (
  <div
    className="absolute inset-0 bg-surface border border-rule rounded-lg p-6 sm:p-8"
    style={{ transform: `rotate(${rotate}deg)` }}
    aria-hidden="true"
  >
    <div className="h-3 w-20 rounded-full bg-ground" />
    <div className="mt-5 space-y-2">
      <div className="h-3 rounded-full bg-ground w-full" />
      <div className="h-3 rounded-full bg-ground w-2/3" />
    </div>
  </div>
);

/**
 * A stack of records, slightly fanned like real paper. The top one holds a
 * block of ciphertext that periodically unscrambles into plain text and
 * back, padlock swinging open and shut in step — driven by one shared
 * `unlocked` toggle so text and lock always move together.
 */
export const SecurityLockAnimation = () => {
  const [unlocked, setUnlocked] = useState(false);

  useEffect(() => {
    if (prefersReducedMotion()) return undefined;
    const id = setInterval(() => setUnlocked((u) => !u), CYCLE_MS);
    return () => clearInterval(id);
  }, []);

  return (
    <div className="relative h-[320px] sm:h-[340px]" aria-hidden="true">
      <BackSheet rotate={-6} />
      <BackSheet rotate={4} />

      <div
        className="absolute inset-0 bg-surface border border-rule rounded-lg p-6 sm:p-8"
        style={{ transform: 'rotate(-1deg)' }}
      >
        <div className="flex items-start justify-between gap-4">
          <p className="pd-mono text-ink-55">YOUR RECORD</p>
          <svg
            width="34"
            height="34"
            viewBox="0 0 40 40"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.6"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="text-brand shrink-0"
            role="presentation"
          >
            <rect x="9" y="19" width="22" height="16" rx="3" />
            <circle cx="20" cy="26" r="1.3" fill="currentColor" stroke="none" />
            <rect x="19.4" y="27.3" width="1.2" height="2.6" fill="currentColor" stroke="none" />
            <path
              d="M13 19 V12 A7 7 0 0 1 27 12 V19"
              className="pd-lock-shackle-anim"
              style={{ transformOrigin: '13px 19px', transform: `rotate(${unlocked ? -32 : 0}deg)` }}
            />
          </svg>
        </div>

        <div className="mt-5 space-y-2">
          {LINES.map((line) => (
            <div key={line} className="text-[16px] leading-[1.7]">
              <ScrambleLine text={line} unlocked={unlocked} />
            </div>
          ))}
        </div>

        <hr className="border-rule my-5" />
        <p className="text-sm text-ink-55">Encrypted the moment it arrives. Only you hold the key.</p>
      </div>
    </div>
  );
};

export default SecurityLockAnimation;
